// 📄 components/StepASNMapping.tsx
import React from 'react';
import { HelpCircle, Package } from 'lucide-react';
import { AsnFormData } from '../../../../../services/commerce/integrationService';

interface StepASNMappingProps {
  cigProjectId: string;
  mapShipNoticeId: boolean;
  mapPackaging: boolean;
  mapBatch: boolean;
  mapPoReference: boolean;
  onChange: (field: keyof AsnFormData, value: any) => void;
  isInvalid: boolean;
}

const MappingToggle = ({
  checked, onChange, label, description, badge,
}: { checked: boolean; onChange: (v: boolean) => void; label: string; description: string; badge?: string }) => (
  <label className="flex items-start gap-4 bg-zinc-900/40 p-3.5 rounded-xl border border-white/5 cursor-pointer hover:bg-white/5 transition-all group">
    <div className="relative mt-0.5 flex-shrink-0">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
      <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-teal-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-white group-hover:text-teal-300 transition-colors">{label}</span>
        {badge && <span className="text-[10px] font-bold bg-teal-500/15 text-teal-400 border border-teal-500/20 px-1.5 py-0.5 rounded-full">{badge}</span>}
      </div>
      <span className="text-xs text-gray-400 mt-0.5 block">{description}</span>
    </div>
  </label>
);

export function StepASNMapping({
  cigProjectId, mapShipNoticeId, mapPackaging, mapBatch, mapPoReference, onChange, isInvalid
}: StepASNMappingProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Ruta SAP / CIG Portal */}
      <div className="bg-teal-500/5 border border-teal-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-teal-400 uppercase tracking-widest block">Portal de Integración / SAP Managed Gateway</span>
        <p className="font-mono text-xs text-gray-300">
          <span>SAP Ariba CIG Portal → Managed Gateway → My Projects → Custom Mapping Manager → ASN / DESADV</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-teal-400">3. CIG Managed Gateway Mapping</h3>
        <p className="text-sm text-gray-400 mt-1">
          Configura los campos específicos de logística en SAP Integration Suite, Managed Gateway.
          Define cuáles campos del documento ASN de Ariba se transformarán hacia los segmentos del IDoc{' '}
          <code className="bg-black/30 px-1 rounded text-teal-300">DELVRY07</code>.
        </p>
      </div>

      {/* Project ID */}
      <div className="bg-white/5 p-4 rounded-xl border border-white/5">
        <div className="max-w-md">
          <label className="block text-xs font-medium text-gray-400 mb-1">
            ID del Proyecto CIG (Project ID) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={cigProjectId}
            onChange={(e) => onChange('cigProjectId', e.target.value.toUpperCase())}
            placeholder="ej. PRJ-123456"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500 ${
              isInvalid && !cigProjectId ? 'border-red-500' : 'border-white/10'
            }`}
          />
          <p className="text-[10px] text-gray-500 mt-1 px-1">
            Disponible en el CIG Portal bajo "My Projects" → selecciona el proyecto de integración correspondiente.
          </p>
        </div>
      </div>

      {/* Toggles de campos logísticos */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-teal-400" />
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Campos Logísticos a Mapear</span>
        </div>

        <MappingToggle
          checked={mapShipNoticeId}
          onChange={(v) => onChange('mapShipNoticeId', v)}
          label="Mapear ShipNoticeID → VBELN / Ref. Entrega"
          description="Propaga el identificador único del ASN de Ariba como referencia externa de la entrega entrante en SAP."
          badge="Recomendado"
        />
        <MappingToggle
          checked={mapPackaging}
          onChange={(v) => onChange('mapPackaging', v)}
          label="Incluir campos de Empaque (Packaging / HU)"
          description="Habilita el mapeo de las Unidades de Manipulación (Handling Units) desde el ASN hacia el IDoc de entrega."
        />
        <MappingToggle
          checked={mapBatch}
          onChange={(v) => onChange('mapBatch', v)}
          label="Incluir campos de Lotes (Batch Management)"
          description="Activa la propagación de números de lote enviados en el ASN hacia las posiciones del IDoc DELVRY07."
        />
        <MappingToggle
          checked={mapPoReference}
          onChange={(v) => onChange('mapPoReference', v)}
          label="Mapear Referencia de Orden de Compra"
          description="Incluye el número de OC de referencia del ASN en el segmento E1EDLV2 del IDoc para trazabilidad."
          badge="Recomendado"
        />
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-yellow-400 flex gap-3 text-sm">
        <HelpCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">XSLT y Campos de Extensión Logística</p>
          <p className="text-xs text-yellow-500/80 mt-0.5">
            Al habilitar campos de Empaque o Lotes, carga el esquema WSDL actualizado en el CIG Portal para que los
            archivos XSLT de transformación ASN→DELVRY07 no generen errores de schema validation durante el procesamiento.
          </p>
        </div>
      </div>
    </div>
  );
}
